import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { billingApi } from '../../../services/billingApi';
import { currentUserHasCerebralAdminRole } from '../../../utils/roleCheck';
import { Alert, Badge, Button } from '../../../components/common';
import { StatTile, StatusBadge, SectionCard, EmptyState } from './components';
import { formatCompactNumber } from './format';
import './BillingAdmin.css';

const STATUS_FILTERS = [
  { id: '', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'disabled', label: 'Disabled' },
  { id: 'suspended', label: 'Suspended' },
];

function relTime(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function summaryNumbers(installation) {
  const summary = installation.summary || installation.currentPeriodSummary || {};
  const queryQty = summary.query?.quantity ?? summary.totals?.query ?? 0;
  const payloadQty = summary.payload?.quantity ?? summary.totals?.payload ?? 0;
  const tokenQty = summary.tokens?.quantity ?? summary.totals?.tokens ?? 0;
  return {
    queryQty: Number(queryQty) || 0,
    payloadMb: Math.round((Number(payloadQty) || 0) / 1024 / 1024),
    tokenQty: Number(tokenQty) || 0,
  };
}

export default function BillingDashboardPage() {
  const navigate = useNavigate();
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    if (!currentUserHasCerebralAdminRole()) {
      navigate('/user-home');
    }
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await billingApi.listInstallations({
        status: statusFilter || undefined,
        pageSize: 100,
      });
      setInstallations(data?.installations || data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load installations');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    let active = 0;
    let alerts = 0;
    let queries = 0;
    let payloadMb = 0;
    installations.forEach((i) => {
      const status = (i.Status || i.status || '').toLowerCase();
      if (status === 'active') active += 1;
      if (i.ReportingAlert || i.reporting_alert) alerts += 1;
      const s = summaryNumbers(i);
      queries += s.queryQty;
      payloadMb += s.payloadMb;
    });
    return { active, alerts, queries, payloadMb };
  }, [installations]);

  return (
    <div className="user-home-layout__page">
      <div className="billing-admin__page-header">
        <div className="billing-admin__header-text">
          <h1 className="user-home-layout__page-title">Billing &amp; Usage</h1>
          <p className="user-home-layout__page-subtitle">
            Customer-cloud installations, their billing status, and current-period usage.
          </p>
        </div>
        <div className="billing-admin__header-actions">
          <Link to="/user-home/admin/billing/onboard">
            <Button variant="primary">+ Onboard Customer</Button>
          </Link>
          <Link to="/user-home/admin/billing/pricing-plans">
            <Button variant="secondary">Pricing Plans</Button>
          </Link>
          <Link to="/user-home/admin/billing/invoices">
            <Button variant="secondary">Invoices</Button>
          </Link>
        </div>
      </div>

      <div className="billing-admin__stats">
        <StatTile
          tone="active"
          label="Active installs"
          value={loading ? <span className="billing-admin__skeleton billing-admin__skeleton--lg" /> : totals.active.toLocaleString()}
          sub={`${installations.length} total`}
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          }
        />
        <StatTile
          tone="alert"
          label="Reporting stale"
          value={loading ? <span className="billing-admin__skeleton billing-admin__skeleton--lg" /> : totals.alerts.toLocaleString()}
          sub={totals.alerts > 0 ? 'Investigate disrupted installs' : 'All installs reporting on time'}
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          }
        />
        <StatTile
          tone="query"
          label="MTD queries"
          value={loading ? <span className="billing-admin__skeleton billing-admin__skeleton--lg" /> : formatCompactNumber(totals.queries)}
          sub="Across all installations"
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          }
        />
        <StatTile
          tone="payload"
          label="MTD payload"
          value={loading ? <span className="billing-admin__skeleton billing-admin__skeleton--lg" /> : `${formatCompactNumber(totals.payloadMb)} MB`}
          sub="Total ingested this month"
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
          }
        />
      </div>

      {error && (
        <Alert variant="error" title="Could not load installations" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <SectionCard
        title="Installations"
        subtitle="Click any row to drill into usage, subscriptions, and audit history."
        toolbar={
          <>
            <div className="billing-admin__segmented" role="tablist" aria-label="Status filter">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.id || 'all'}
                  type="button"
                  className={`billing-admin__segmented-btn ${statusFilter === f.id ? 'is-active' : ''}`}
                  onClick={() => setStatusFilter(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <Button variant="ghost" onClick={load} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </>
        }
        flush
      >
        <div className="billing-admin__table-wrap">
          <table className="billing-admin__table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Status</th>
                <th>Last heartbeat</th>
                <th className="num">Queries (MTD)</th>
                <th className="num">Payload MB</th>
                <th className="num">Tokens</th>
                <th>Alerts</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={`sk-${idx}`} className="billing-admin__skeleton-row">
                    {Array.from({ length: 8 }).map((__, c) => (
                      <td key={c}><span className="billing-admin__skeleton" /></td>
                    ))}
                  </tr>
                ))
              )}
              {!loading && installations.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 0, border: 0 }}>
                    <EmptyState
                      icon={
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <rect x="2" y="5" width="20" height="14" rx="2" />
                          <line x1="2" y1="10" x2="22" y2="10" />
                        </svg>
                      }
                      title="No installations yet"
                      message="Onboard your first customer to provision an installation, generate an HMAC secret, and start metering usage."
                      action={
                        <Link to="/user-home/admin/billing/onboard">
                          <Button variant="primary">+ Onboard Customer</Button>
                        </Link>
                      }
                    />
                  </td>
                </tr>
              )}
              {!loading && installations.map((i) => {
                const status = (i.Status || i.status || 'active').toLowerCase();
                const summary = summaryNumbers(i);
                const alert = i.ReportingAlert || i.reporting_alert;
                const installId = i.InstallationId || i.installation_id;
                return (
                  <tr key={installId}>
                    <td>
                      <div className="billing-admin__table-cell-primary">
                        <Link className="billing-admin__table-row-link" to={`/user-home/admin/billing/installations/${installId}`}>
                          {i.Label || i.label || '(no label)'}
                        </Link>
                      </div>
                      <div className="billing-admin__table-cell-meta">{i.Environment || i.environment || '—'}</div>
                    </td>
                    <td><StatusBadge status={status} /></td>
                    <td>{relTime(i.LastHeartbeatAt || i.last_heartbeat_at)}</td>
                    <td className="num">{formatCompactNumber(summary.queryQty)}</td>
                    <td className="num">{formatCompactNumber(summary.payloadMb)}</td>
                    <td className="num">{formatCompactNumber(summary.tokenQty)}</td>
                    <td>{alert ? <Badge variant="danger">Reporting stale</Badge> : <span className="billing-admin__muted">—</span>}</td>
                    <td className="num">
                      <Link className="billing-admin__table-row-link" to={`/user-home/admin/billing/installations/${installId}`}>
                        Open →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
